import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DashboardEditor from './DashboardEditor';
import _ from 'lodash';
import { useTranslation } from 'localization/useTranslation';
import {
  Alert,
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  MenuToggle,
  Spinner,
  Stack,
  StackItem,
  TextInput,
  Toolbar,
  ToolbarGroup,
  ToolbarContent,
  ToolbarItemVariant,
  ToolbarItem,
  Switch
} from '@patternfly/react-core';

import ThIcon from '@patternfly/react-icons/dist/js/icons/th-icon';

import NodeContext from '../../services/Context/NodeContext';
import styles from './DashboardEditorPage.module.css';
import { panelObjects } from './panels';
import useBoolean from '../../utils/hooks/useBoolean';
import ContentPage from '../ContentView/ContentPage/ContentPage';
import NodeTreePage from '../NodePage/NodeTreePage';
import BreadcrumbSkeleton from 'components/Breadcrumb/BreadcrumbSkeleton';
import { addModelsAndAssetsToDashboard } from './DashboardUtils';
import { adjustDashboard, useDashboardQuery, useUpdateDashboard } from './useDashboardsQuery';
import { privileges } from 'utils/types';
import ConfirmModalWithOrg from 'components/Modals/ConfirmModalWithOrg';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { createPathUrl, parseLocation } from 'components/NodePage/location';

export const DashboardName = ({ dashboardId }) => {
  const dashboardQuery = useDashboardQuery(dashboardId);
  const dashboard = dashboardQuery.data;
  return dashboardQuery.isLoading ? <BreadcrumbSkeleton /> : (dashboard?.displayName ?? dashboard?.name ?? '');
};

const useLocationQuery = () => {
  const { search } = useLocation();
  return useMemo(() => {
    return new URLSearchParams(search);
  }, [search]);
};

const DashboardEditorPage = ({ breadcrumbs }) => {
  const T = useTranslation();
  const params = useParams();
  const { dashboardId } = params;
  const [isOpen, showPublishDialog, hidePublishDialog] = useBoolean();

  const history = useHistory();
  const queryParams = useLocationQuery();
  const location = useLocation();
  const fetchingData = queryParams.get('fetchingData') === 'true';
  const selectedPathName = queryParams.get('nodePath');
  const [dashboard, setDashboard] = useState({});
  const [originalDashboard, setOriginalDashboard] = useState(null);
  const [hasUnsavedContent, setHasUnsavedContent] = useState((dashboardId === null) ? true : false);

  const dashboardQuery = useDashboardQuery(dashboardId);

  const saveDashboard = useUpdateDashboard();
  const _saveDashboard = useCallback((dashboard) => {
    saveDashboard.mutate(dashboard, {
      onSuccess: (result, savedDashboard) => {
        setOriginalDashboard(_.cloneDeep(savedDashboard));
        setDashboard(savedDashboard);
        history.replace('/admin/dashboards/' + (result.id ?? savedDashboard.id));
      }
    });
  }, [history, saveDashboard]);

  useEffect(() => {
    setHasUnsavedContent(!_.isEqual(dashboard, originalDashboard));
  }, [dashboard, dashboard.data?.panels, originalDashboard]);

  useEffect(() => {
    if (dashboardQuery.data != null) {
      setDashboard(adjustDashboard(dashboardQuery.data));
      setOriginalDashboard(_.cloneDeep(dashboardQuery.data));
    }
  }, [dashboardId, dashboardQuery.data]);

  const onDisplayNameChange = useCallback(displayName => {
    setDashboard(oldDashboard => ({ ...oldDashboard, displayName }));
  }, []);

  const createPanel = useRef(null);
  const [isPanelSelectionOpen, , hidePanelSelection, togglePanelSelection] = useBoolean(false);

  const onCreatePanel = useCallback((panelTypeIndex) => {
    hidePanelSelection();
    createPanel.current?.(panelTypeIndex);
  }, [hidePanelSelection]);

  const [selectedNode, setSelectedNode] = useState(null);
  const onTreeItemSelected = useCallback((node) => {
    const search = new URLSearchParams(location.search);
    search.set('nodePath', '/' + createPathUrl(node));
    history.replace({ search: search.toString() });

    setSelectedNode(node);
  }, [history, location.search]);
  const nodeData = useMemo(() => {
    if (fetchingData) {
      return { node: selectedNode };
    }
    return { node: [] };
  }, [fetchingData, selectedNode]);

  const pathUrl = selectedPathName ?? (selectedNode && createPathUrl(selectedNode));
  const { resourceIds } = useMemo(() => parseLocation(pathUrl ?? ''), [pathUrl]);


  const panelItems = useMemo(() => {
    return _.map(panelObjects, ({ info }, id) => (
      <DropdownItem key={id} icon={info.icon} onClick={() => onCreatePanel(id)}>
        { T.localize(info.label) }
      </DropdownItem>
    ));
  }, [T, onCreatePanel]);

  const onUpdateDashboard = useCallback((updatedDashboard) => {
    const modifiedDashboard = addModelsAndAssetsToDashboard(updatedDashboard);
    // TODO: This should probably be updated to new version during migration step
    if (modifiedDashboard.data.version == null || modifiedDashboard.data.version < 2) {
      setDashboard({ ...modifiedDashboard, data: { ...modifiedDashboard.data, version: 2 } });
    } else {
      setDashboard(modifiedDashboard);
    }
  }, []);

  const checkOrg = useCallback(() => {
    if (dashboardId) {
      _saveDashboard(dashboard);
    } else {
      showPublishDialog();
    }
  }, [_saveDashboard, dashboard, dashboardId, showPublishDialog]);

  const onPublishToOrg = useCallback((dashboard, currentOrg) => {
    hidePublishDialog();
    _saveDashboard({
      ...dashboard,
      organizationId: currentOrg.id
    });
  }, [_saveDashboard, hidePublishDialog]);

  const handleFetchChange = useCallback((unusedEvent, checked) => {
    const search = new URLSearchParams(location.search);
    search.set('fetchingData', checked);
    history.push({ search: search.toString() });
  }, [history, location.search]);

  const contentToolbar = useMemo(() => {
    return (
      <Toolbar>
        <ToolbarContent>
          <ToolbarGroup>
            <ToolbarItem>
              <Switch
                isReversed
                id="dashboardEnabled"
                label="fetching data"
                labelOff="paused fetching data"
                isChecked={fetchingData}
                onChange={handleFetchChange}
              />
            </ToolbarItem>
          </ToolbarGroup>
          <ToolbarGroup>
            <ToolbarItem>
              <TextInput id="displayName" value={dashboard.displayName ?? dashboard.name ?? ''} onChange={(_event, value) => onDisplayNameChange(value)} placeholder={T.common.name}/>
            </ToolbarItem>
            <ToolbarItem>
              <Button onClick={checkOrg} isDisabled={!hasUnsavedContent} isLoading={saveDashboard.isLoading}>{ T.common.save }</Button>
            </ToolbarItem>
            <ToolbarItem variant={ToolbarItemVariant.pagination} >
              <Dropdown popperProps={{ position: 'right' }} toggle={(toggleRef) => (
                <MenuToggle
                  aria-label="Toggle"
                  ref={toggleRef}
                  variant="plain"
                  onClick={togglePanelSelection}
                  isExpanded={isPanelSelectionOpen}
                  style={{ width: 'auto' }}
                >
                  <ThIcon />
                </MenuToggle>
              )
              } isOpen={isPanelSelectionOpen}>
                <DropdownList>
                  { panelItems }
                </DropdownList>
              </Dropdown>
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>
    );
  }, [T.common.name, T.common.save, checkOrg, dashboard.displayName, dashboard.name, fetchingData, handleFetchChange, hasUnsavedContent, isPanelSelectionOpen, onDisplayNameChange, panelItems, saveDashboard.isLoading, togglePanelSelection]);

  if (dashboardQuery.isError) {
    return (<Alert variant="danger" isInline title={T.dashboardspage.editor.error.failedload}/>);
  }

  return (
    <NodeContext.Provider value={nodeData}>
      <ContentPage breadcrumb={breadcrumbs} toolbar={contentToolbar} help="admin/dashboards/create">
        { dashboardQuery.isInitialLoading && <Spinner /> }
        { !dashboardQuery.isInitialLoading &&
          <NodeTreePage initialExpandedPath={resourceIds?.treePath} onItemSelected={onTreeItemSelected} panelTitle={T.admin.dashboardspage.selecttestnode}>
            <Stack className={styles.dashboardEditorPage}>
              <StackItem isFilled className={styles.dashboardEditorContent}>
                <DashboardEditor dashboard={dashboard} onDashboardUpdated={onUpdateDashboard}>{ ({ createPanel: createPanelIn }) => {
                  // TODO: Maybe should use context here instead.
                  createPanel.current = createPanelIn;
                } }
                </DashboardEditor>
              </StackItem>
            </Stack>
          </NodeTreePage>
        }
        <ConfirmModalWithOrg
        // TODO: Translate
          title={'Publish to Organization'}
          icon={'info'}
          isOpen={isOpen}
          item={dashboard}
          confirmText={T.admin.common.publish}
          onConfirm={onPublishToOrg}
          onCancel={hidePublishDialog}
          template={'Connect this dashboard to below organization.'}
          args={[dashboard?.displayName]}
          neededPrivilege={privileges.DashboardManagement}
        />
      </ContentPage>
    </NodeContext.Provider>
  );
};

export default DashboardEditorPage;
